import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation, useNavigate, useParams, useSearchParams, useOutletContext } from 'react-router-dom';
import './App.css';
import useIdleLogout from './hooks/useIdleLogout.js';
import { resolveTimeoutMs, saveLastPath, getLastPath } from './lib/sessionPolicy.js';
import buildInfo from './buildInfo.json';

// --- Firebase ---
import { db, auth, storage } from './firebase/firebase-config.js'; 
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";

// --- Vistas ---
import KioskView from './views/KioskView.js';
import NewJobView from './views/NewJobView.js';
import NewJobLiteView from './views/NewJobLiteView.js';
import NewJobStartView from './views/NewJobStartView.js';
import JobView from './views/JobView.js';
import DashboardView from './views/DashboardView.js';
import TeamAccessView from './views/TeamAccessView.js';
import ClientsView from './views/ClientsView.js';
import ScanningStationView from './views/ScanningStationView.js';
import TerminalView from './views/TerminalView.js';
import FileFinderView from './views/FileFinderView.js';
import MaterialsView from './views/MaterialsView.js';
import ProductsView from './views/ProductsView.js';
// FASE 0: QuoteView (1 pieza) fuera del bundle activo — la ruta /quote forwardea a /quote-product.
// RESERVADO para el fast-mode (Fase 1). NO borrar el archivo ni la CF getQuote.
// import QuoteView from './views/QuoteView.js';
import ProductQuoteView from './views/ProductQuoteView.js';
import EstimatesView from './views/EstimatesView.js';
import NewGangRunView from './views/NewGangRunView.js';
import MachinesView from './views/MachinesView.js';
import TradeshowCRMView from './views/TradeshowCRMView.js';
import WorkQueueView from './views/WorkQueueView.js';
import MyProfileView from './views/MyProfileView.js';
import KioskSettingsView from './views/KioskSettingsView.js';
import ConfiguradorPreciosView from './views/ConfiguradorPreciosView.js';
import GeneralAiChatModal from './components/GeneralAiChatModal';
import RouteErrorBoundary from './components/RouteErrorBoundary.js';
import PasswordRotationBanner from './components/PasswordRotationBanner.js';
import { t } from './lib/i18n';

// React.lazy: primer uso en el repo (main.js ya está en su techo de 2.27MB —
// doctrina/presupuesto-complejidad.md — code-splitting obligatorio antes de
// crecer). Ruta nueva y aislada: el mejor lugar para estrenar el patrón.
const CajaDiariaView = React.lazy(() => import('./views/CajaDiariaView.js'));
// S4c: /novedades y /feedback también lazy (mismo motivo: presupuesto de main.js).
const NovedadesView = React.lazy(() => import('./views/NovedadesView.js'));
const FeedbackView = React.lazy(() => import('./views/FeedbackView.js'));

// --- Iconos ---
const IconDashboard = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"></path></svg>;
const IconNewJob = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"></path></svg>;
const IconRoles = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0012 11z" clipRule="evenodd"></path></svg>;
const IconClients = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zm-1.5 5.5a3 3 0 00-3 0V12a2 2 0 00-2 2v1a2 2 0 002 2h3.5a2 2 0 002-2v-1a2 2 0 00-2-2v-.5zM15.5 6a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm-2.5 5.5a3 3 0 00-3 0V12a2 2 0 00-2 2v2.5a.5.5 0 001 0V14a1 1 0 011-1h3a1 1 0 011 1v.5a.5.5 0 001 0V12a2 2 0 00-2-2v-.5z"></path></svg>;
const IconMaterials = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"></path></svg>;
const IconProducts = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9 2a1 1 0 00-.894.553L7.382 4H4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 9a3 3 0 116 0 3 3 0 01-6 0z"></path></svg>;
const IconMachines = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.532 1.532 0 012.287-.947c1.372.836 2.942-.734-2.106-2.106a1.532 1.532 0 01-.947-2.287c1.561-.379-1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"></path></svg>;
const IconLogout = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd"></path></svg>;
const IconScan = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M4 4m0 1a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4zM4 14m0 1a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-1zM14 4m0 1a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1V4zM14 14m0 1a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1h-1a1 1 0 0 1-1-1v-1z M3 9a1 1 0 0 1 1-1h12a1 1 0 1 1 0 2H4a1 1 0 0 1-1-1z"/></svg>;
const IconFileSearch = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"></path></svg>;
const IconCRM = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"></path></svg>;
const IconSidebarCollapse = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"></path></svg>;
const IconProfile = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>;
const IconUsers = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zm-1.5 5.5a3 3 0 00-3 0V12a2 2 0 00-2 2v1a2 2 0 002 2h3.5a2 2 0 002-2v-1a2 2 0 00-2-2v-.5zM15.5 6a1.5 1.5 0 100-3 1.5 1.5 0 000 3zm-2.5 5.5a3 3 0 00-3 0V12a2 2 0 00-2 2v2.5a.5.5 0 001 0V14a1 1 0 011-1h3a1 1 0 011 1v.5a.5.5 0 001 0V12a2 2 0 00-2-2v-.5z"></path></svg>;
const IconKioskSettings = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.532 1.532 0 012.287-.947c1.372.836 2.942-.734-2.106-2.106a1.532 1.532 0 01-.947-2.287c1.561-.379-1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd"></path></svg>;
const IconQuote = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"></path></svg>;
const IconEstimate = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V4a2 2 0 00-2-2H6zm1 2h6v2H7V4zm0 4h6a1 1 0 110 2H7a1 1 0 010-2zm0 4h3a1 1 0 110 2H7a1 1 0 010-2z" clipRule="evenodd"></path></svg>;
const IconPricing = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 16a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 7.323V16h2a1 1 0 110 2H7a1 1 0 110-2h2V7.323L6.237 8.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 16a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.616a1 1 0 01.894-1.79l1.599.8L9 4.323V3a1 1 0 011-1z" clipRule="evenodd"></path></svg>;
const IconCola = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M3 4a1 1 0 011-1h3a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm9 0a1 1 0 011-1h3a1 1 0 011 1v7a1 1 0 01-1 1h-3a1 1 0 01-1-1V4z"></path></svg>;
const IconCajaDiaria = () => <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"></path><path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1z" clipRule="evenodd"></path></svg>;

const LoginScreen = ({ handleLogin }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const onSubmit = (e) => { e.preventDefault(); setError(''); handleLogin(email, password, setError); };
    return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
            <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
                <img src={`${process.env.PUBLIC_URL}/cartonbar-logo.png`} alt="CARTONBAR" className="h-16 object-contain mx-auto" />
                <h2 className="text-xl font-semibold text-center text-gray-700">Iniciar Sesión</h2>
                <form onSubmit={onSubmit} className="space-y-6">
                    <div><label htmlFor="email" className="text-sm font-bold text-gray-600 block">Email</label><input id="email" type="email" required className="w-full p-2 mt-1 border border-gray-300 rounded-md shadow-sm" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
                    <div><label htmlFor="password"  className="text-sm font-bold text-gray-600 block">Contraseña</label><input id="password" type="password" required className="w-full p-2 mt-1 border border-gray-300 rounded-md shadow-sm" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
                    {error && <p className="text-sm text-red-600 text-center">{error}</p>}
                    <div><button type="submit" className="w-full px-4 py-2 font-bold text-white bg-blue-600 rounded-md hover:bg-blue-700">Entrar</button></div>
                </form>
            </div>
        </div>
    );
};

// Indicador de versión (2026-08-04): un hash sin fecha no dice si es viejo —
// el bloqueo de Alana fue producción sirviendo un commit de 3 atrás y costó 2
// días de auditoría descubrir que el código estaba bien y el despliegue no.
// Lee buildInfo.json (ya horneado en el bundle en build time) — CERO red, CERO
// Firestore.
const MES3_BUILD = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
function fmtFechaBuild(d) {
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${d.getDate()}-${MES3_BUILD[d.getMonth()]} ${hh}:${mm}`;
}

// AF&B F1 (punto 5) — El sidebar era `w-64` FIJO sin ningun breakpoint: en un telefono de
// 390px se comia 256px y dejaba 134px de contenido. Ahora, por debajo de `md` (768px), vive
// como cajon off-canvas: oculto por defecto, se abre con el boton de la barra movil de
// MainLayout y se cierra al navegar o al tocar el fondo. En `md` y arriba NADA cambia:
// vuelve a ser la columna estatica de siempre con su colapso `w-20`/`w-64`.
const Sidebar = ({ onLogout, user, isCollapsed, toggleSidebar, isMobileOpen, closeMobile }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [hashCopiado, setHashCopiado] = useState(false);
    const fechaBuild = new Date(buildInfo.builtAt);
    async function copiarHashCompleto() {
        try {
            await navigator.clipboard.writeText(buildInfo.commitFull || buildInfo.commit);
            setHashCopiado(true);
            setTimeout(() => setHashCopiado(false), 1500);
        } catch (_) { /* portapapeles no disponible — sin feedback, no rompe nada */ }
    }
    // Menú agrupado por dominio (estilo ERP). "Mi Perfil" y "Cerrar Sesión" van al
    // pie (zona del usuario), no entre las secciones de trabajo.
    const navSections = [
        {
            title: 'Operación',
            items: [
                { id: 'dashboard', path: '/dashboard', label: 'Dashboard', icon: <IconDashboard />, perm: 'canViewDashboard' },
                { id: 'newJob', path: '/jobs/new', label: 'Nuevo Trabajo', icon: <IconNewJob />, perm: 'canCreateJobs' },
                // FASE 0 (canonizar multipieza): el cotizador de 1 pieza sale del nav. La ruta /quote
                // queda RESERVADA como futuro deep-link "fast mode" (Fase 1) y hoy forwardea a
                // /quote-product (ver Routes). NO borrar QuoteView ni la CF getQuote.
                // { id: 'quote', path: '/quote', label: 'Cotizador', icon: <IconQuote />, perm: 'canCreateQuotes' },
                { id: 'quoteProduct', path: '/quote-product', label: 'Cotizador multipieza', icon: <IconQuote />, perm: 'canCreateQuotes' },
                { id: 'estimates', path: '/estimates', label: 'Estimados', icon: <IconEstimate />, perm: 'canCreateQuotes' },
                { id: 'tradeshowCRM', path: '/crm', label: 'CRM Eventos', icon: <IconCRM />, perm: 'canAccessTradeshowCRM' },
                { id: 'cola', path: '/cola', label: 'Cola de Trabajo', icon: <IconCola />, perm: 'canViewDashboard' },
                { id: 'terminal', path: '/terminal', label: 'Terminal de Operador', icon: <IconScan />, perm: 'canScanProduction' },
                { id: 'scan', path: '/scan', label: 'Estación de Escaneo', icon: <IconScan />, perm: 'canScanProduction' },
                { id: 'fileFinder', path: '/file-finder', label: 'Buscador de Archivos', icon: <IconFileSearch />, perm: 'canScanProduction' },
            ],
        },
        {
            title: 'Catálogos',
            items: [
                { id: 'clients', path: '/clients', label: 'Clientes', icon: <IconClients />, perm: 'canViewClients' },
                { id: 'materials', path: '/materials', label: 'Materiales', icon: <IconMaterials />, perm: 'canViewMaterials' },
                { id: 'products', path: '/products', label: 'Productos', icon: <IconProducts />, perm: 'canViewMaterials' },
                { id: 'machines', path: '/machines', label: 'Máquinas', icon: <IconMachines />, perm: 'canManageMachines' },
            ],
        },
        {
            title: 'Administración',
            items: [
                // Usuarios y Roles unificados en una sola pantalla con pestañas.
                { id: 'team', path: user.permissions?.canManageUsers ? '/users' : '/roles', label: 'Usuarios y Roles', icon: <IconUsers />, anyPerm: ['canManageUsers', 'canManageRoles'], activePaths: ['/users', '/roles'] },
                { id: 'kioskSettings', path: '/settings/kiosk', label: 'Ajustes del Kiosko', icon: <IconKioskSettings />, perm: 'canManageKiosk' },
                { id: 'pricing', path: '/settings/pricing', label: 'Configurar Precios', icon: <IconPricing />, perm: 'canManagePricing' },
                { id: 'cajaDiaria', path: '/caja-diaria', label: 'Caja Diaria', icon: <IconCajaDiaria />, perm: 'canManageInvoicing' },
            ],
        },
        {
            // S4c: visible para TODO usuario autenticado (sin `perm`).
            title: t('nav.help'),
            items: [
                { id: 'novedades', path: '/novedades', label: t('nav.novedades'), icon: <IconEstimate /> },
                { id: 'feedback', path: '/feedback', label: t('nav.feedback'), icon: <IconCRM /> },
            ],
        },
    ];
    const canSee = (item) => item.anyPerm
        ? item.anyPerm.some((p) => user.permissions?.[p])
        : (item.perm ? !!user.permissions?.[item.perm] : true);
    const isActive = (item) => (item.activePaths || [item.path]).some((p) => location.pathname.startsWith(p));
    const visibleSections = navSections
        .map((s) => ({ ...s, items: s.items.filter(canSee) }))
        .filter((s) => s.items.length > 0);

    const navButtonClass = (active) =>
        `w-full text-left flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${isCollapsed ? 'justify-center' : ''} ${active ? 'bg-blue-600 text-white' : 'hover:bg-gray-700'}`;

    // OJO con las clases de display: el estado cerrado tiene que ser exactamente
    // `hidden md:flex` (patron canonico de Tailwind). Dejar un `flex` suelto en la base y
    // confiar en que `hidden` lo pise es fragil: ambas reglas viven en la misma capa.
    return (
        <aside className={`bg-gray-800 text-white flex-col flex-shrink-0 transition-all duration-300 md:flex md:static md:z-auto md:shadow-none ${isMobileOpen ? 'flex fixed inset-y-0 left-0 z-50 shadow-2xl' : 'hidden'} ${isCollapsed ? 'w-20' : 'w-64'}`}>
            <div className="p-3 border-b border-gray-700">
                {isCollapsed ? (
                    <div className="flex flex-col items-center gap-2">
                        <div className="bg-white rounded-md p-1">
                            <img src={`${process.env.PUBLIC_URL}/cartonbar-icon.png`} alt="CARTONBAR" className="h-8 w-8 object-contain" />
                        </div>
                        <button onClick={toggleSidebar} className="p-1 rounded-md hover:bg-gray-700">
                            <IconSidebarCollapse />
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between gap-2">
                            <div className="bg-white rounded-md px-2 py-1">
                                <img src={`${process.env.PUBLIC_URL}/cartonbar-logo.png`} alt="CARTONBAR" className="h-7 object-contain" />
                            </div>
                            <button onClick={toggleSidebar} className="p-1 rounded-md hover:bg-gray-700 flex-shrink-0">
                                <IconSidebarCollapse />
                            </button>
                        </div>
                        <div className="min-w-0 mt-3" title={user?.email}>
                            <p className="text-[10px] uppercase tracking-wide text-gray-400 leading-none mb-0.5">Conectado como</p>
                            <h1 className="text-sm font-bold truncate leading-tight">{user?.displayName || user?.name || user?.email || 'Cargando...'}</h1>
                        </div>
                    </>
                )}
            </div>
            <nav className="flex-grow p-4 space-y-4 overflow-y-auto">
                {visibleSections.map((section) => (
                    <div key={section.title} className="space-y-1">
                        {!isCollapsed && <p className="px-3 text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">{section.title}</p>}
                        {section.items.map((item) => (
                            <button key={item.id} type="button" title={isCollapsed ? item.label : ''} className={navButtonClass(isActive(item))} onClick={() => { navigate(item.path); closeMobile?.(); }}>
                                {item.icon}
                                {!isCollapsed && <span>{item.label}</span>}
                            </button>
                        ))}
                    </div>
                ))}
            </nav>
            <div className="p-4 border-t border-gray-700 space-y-2">
                <button onClick={() => { navigate('/profile'); closeMobile?.(); }} type="button" title={isCollapsed ? 'Mi Perfil' : ''} className={navButtonClass(location.pathname.startsWith('/profile'))}>
                    <IconProfile />
                    {!isCollapsed && <span>Mi Perfil</span>}
                </button>
                <button onClick={onLogout} title={isCollapsed ? 'Cerrar Sesión' : ''} className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 ${isCollapsed ? 'justify-center' : ''}`}>
                    <IconLogout />
                    {!isCollapsed && <span>Cerrar Sesión</span>}
                </button>
                <p
                    onClick={copiarHashCompleto}
                    title={hashCopiado ? '¡Copiado!' : `Clic para copiar el hash completo · ${buildInfo.commitFull || buildInfo.commit}`}
                    className={`text-[10px] text-gray-500 hover:text-gray-300 text-center pt-1 cursor-pointer transition-colors ${isCollapsed ? 'truncate' : ''}`}
                >
                    {hashCopiado ? '¡Copiado!' : (isCollapsed ? `v ${buildInfo.commit}` : `v ${buildInfo.commit} · ${fmtFechaBuild(fechaBuild)}`)}
                </p>
            </div>
        </aside>
    );
};

const MainLayout = ({ user, onLogout, onOpenGeneralAIChat }) => {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    // AF&B F1 (punto 5): cajon del sidebar en movil (< md). Solo aplica por debajo de 768px.
    const [isSidebarMobileOpen, setIsSidebarMobileOpen] = useState(false);
    const location = useLocation();
    // Recuerda la última pantalla (por máquina) para volver ahí tras re-ingresar.
    useEffect(() => {
        saveLastPath(location.pathname + (location.search || ''));
        // Cambiar de pantalla cierra el cajon: en movil el sidebar tapa el contenido.
        setIsSidebarMobileOpen(false);
    }, [location.pathname, location.search]);
    // Higiene de sesión: cierra sesión por inactividad para que las acciones no queden
    // registradas con un usuario equivocado. El intervalo depende del TIPO DE ESTACIÓN
    // (por máquina) o, si no se fijó, del ROL del usuario (ver lib/sessionPolicy.js).
    const { warning, secondsLeft, stayActive } = useIdleLogout({ onLogout, timeoutMs: resolveTimeoutMs(user) });
    return (
        <div className="flex min-h-screen md:h-screen bg-gray-100">
            <Sidebar
                user={user}
                onLogout={onLogout}
                isCollapsed={isSidebarCollapsed}
                toggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                isMobileOpen={isSidebarMobileOpen}
                closeMobile={() => setIsSidebarMobileOpen(false)}
            />
            {/* Fondo oscuro del cajon: solo existe en movil y solo con el cajon abierto. */}
            {isSidebarMobileOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                    onClick={() => setIsSidebarMobileOpen(false)}
                    aria-hidden="true"
                />
            )}
            {/* `min-w-0` es obligatorio: sin el, un hijo ancho (tabla, kanban) estira el flex
                item y reaparece el scroll horizontal que se intenta eliminar. */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Barra movil: solo < md. Empuja el contenido, no lo tapa (nada de FAB
                    flotante encima de los titulos de cada vista). */}
                <header className="md:hidden flex items-center gap-3 bg-gray-800 text-white px-3 py-2 flex-shrink-0">
                    <button
                        type="button"
                        onClick={() => setIsSidebarMobileOpen(true)}
                        className="p-2 -ml-1 rounded-md hover:bg-gray-700"
                        aria-label="Abrir menú"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    </button>
                    <div className="bg-white rounded-md px-2 py-1">
                        <img src={`${process.env.PUBLIC_URL}/cartonbar-logo.png`} alt="CARTONBAR" className="h-6 object-contain" />
                    </div>
                </header>
                {/* S4c: banner persistente de rotación de contraseña (ES+EN) hasta re-login. */}
                <PasswordRotationBanner user={user} />
                <main className="flex-1 md:min-h-0 md:overflow-y-auto">
                    <Outlet context={{ onOpenGeneralAIChat }} />
                </main>
            </div>
            {warning && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100]">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 text-center">
                        <h2 className="text-xl font-bold text-gray-800 mb-2">¿Sigues ahí?</h2>
                        <p className="text-gray-600 mb-1">
                            Tu sesión (<strong>{user?.displayName || user?.email}</strong>) se cerrará por inactividad en
                        </p>
                        <p className="text-4xl font-extrabold text-red-600 mb-4 tabular-nums">{secondsLeft}s</p>
                        <p className="text-xs text-gray-500 mb-6">
                            Esto evita que las acciones queden registradas con el usuario equivocado.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button onClick={onLogout} className="py-2 px-5 font-bold bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">
                                Cerrar sesión
                            </button>
                            <button onClick={stayActive} className="py-2 px-5 font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                                Seguir conectado
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const ProtectedRoute = ({ user, children }) => {
    if (!user) {
        return <Navigate to="/login" replace />;
    }
    return children;
};

const PermissionRoute = ({ user, permission, children }) => {
    if (!user.permissions?.[permission]) {
        return (
            <div className="p-4">
                <h1 className="text-3xl font-bold text-red-600">Acceso Denegado</h1>
                <p>No tienes permiso para ver esta sección.</p>
            </div>
        );
    }
    return children;
};

const NewJobWrapper = ({ user, db, storage }) => {
    const { jobId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [initialJobData, setInitialJobData] = useState(null);
    const [isLoading, setIsLoading] = useState(!!jobId);
    const [error, setError] = useState(null);
    const { onOpenGeneralAIChat } = useOutletContext();

    useEffect(() => {
        if (!jobId) {
            // Trabajo nuevo: preselecciona el tipo si viene del selector (?type=Servicio|Proyecto).
            const t = searchParams.get('type');
            if (t) setInitialJobData({ type: t });
            return;
        }

        const fetchJobForEdit = async () => {
            try {
                const jobDocRef = doc(db, "jobs", jobId);
                const jobDocSnap = await getDoc(jobDocRef);
                if (jobDocSnap.exists()) {
                    setInitialJobData({ id: jobDocSnap.id, ...jobDocSnap.data() });
                } else {
                    setError("El trabajo que intentas editar no fue encontrado.");
                }
            } catch (err) {
                console.error("Error fetching job for edit:", err);
                setError("Ocurrió un error al cargar los datos del trabajo.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchJobForEdit();
    }, [jobId, db, searchParams]);

    if (isLoading) {
        return <div className="p-8 text-center">Cargando editor...</div>;
    }
    if (error) {
        return <div className="p-8 text-center text-red-500">{error}</div>;
    }

    return <NewJobView user={user} db={db} storage={storage} initialJobData={initialJobData} onOpenGeneralAIChat={onOpenGeneralAIChat} />;
};

function App() {
    const [user, setUser] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isGeneralChatModalOpen, setIsGeneralChatModalOpen] = useState(false);
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (userAuth) => {
            if (userAuth) {
                const userDocRef = doc(db, "users", userAuth.uid);
                const userDocSnap = await getDoc(userDocRef);
                
                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    let effectivePermissions = {};
                    if (userData.roles && userData.roles.length > 0) {
                        try {
                            const rolesQuery = query(collection(db, "roles"), where("name", "in", userData.roles));
                            const rolesSnapshot = await getDocs(rolesQuery);
                            rolesSnapshot.forEach(roleDoc => {
                                const rolePermissions = roleDoc.data().permissions;
                                // Unión PERMISIVA entre roles: un `true` de cualquier rol nunca
                                // se revierte por un `false` de otro (orden de Firestore en `in`
                                // no está garantizado; Object.assign dejaba ganar al último rol
                                // devuelto, no al más permisivo — bug real: Alana con Diseño+Ventas
                                // quedaba sin canCreateJobs pese al `true` de Diseño).
                                if (rolePermissions) {
                                    Object.keys(rolePermissions).forEach(k => {
                                        if (rolePermissions[k] === true) effectivePermissions[k] = true;
                                    });
                                }
                            });
                        } catch(e) { console.error("Error fetching roles:", e); }
                    }
                    setUser({ uid: userAuth.uid, email: userAuth.email, ...userData, permissions: effectivePermissions });
                } else {
                    setUser({ uid: userAuth.uid, email: userAuth.email, permissions: {} });
                }

                const usersSnapshot = await getDocs(collection(db, "users"));
                setAllUsers(usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            } else {
                setUser(null);
                setAllUsers([]);
            }
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleLogin = async (email, password, setError) => {
        try { await signInWithEmailAndPassword(auth, email, password); }
        catch (error) {
            // Log del error real de Firebase para diagnóstico (code + message).
            console.error("Error de inicio de sesión:", error?.code, error?.message, error);
            const map = {
                'auth/invalid-credential': 'Credenciales incorrectas.',
                'auth/wrong-password': 'Credenciales incorrectas.',
                'auth/user-not-found': 'No existe un usuario con ese correo.',
                'auth/invalid-email': 'El correo no tiene un formato válido.',
                'auth/user-disabled': 'Esta cuenta está deshabilitada.',
                'auth/too-many-requests': 'Demasiados intentos. Espera unos minutos e inténtalo de nuevo.',
                'auth/network-request-failed': 'Sin conexión con el servidor de autenticación.',
                'auth/api-key-not-valid': 'La clave de API de Firebase no es válida para este dominio.',
                'auth/api-key-expired': 'La clave de API de Firebase está expirada. Reinicia el servidor para tomar la clave nueva.',
                'auth/requests-from-referer-are-blocked': 'Las peticiones desde este dominio (localhost) están bloqueadas en la clave de API.',
            };
            const code = error?.code || '';
            const fallback = code.includes('referer') || code.includes('api-key')
                ? 'Peticiones desde localhost bloqueadas por la clave de API de Firebase.'
                : 'Credenciales incorrectas.';
            setError(map[code] || fallback);
        }
    };

    const handleLogout = async () => { await signOut(auth); };
    
    if (isLoading) { return <div className="flex items-center justify-center h-screen">Cargando...</div>; }

    return (
        <>
            <Router>
                <Routes>
                    <Route path="/login" element={!user ? <LoginScreen handleLogin={handleLogin} /> : <Navigate to={getLastPath() || "/"} replace />} />
                    <Route path="/kiosk" element={<KioskView />} />
                    {/* Terminal de Operador (W2 · B1c): pantalla completa SIN sidebar, gateada
                        por canScanProduction. Hermana de /kiosk para quedar fuera de MainLayout. */}
                    <Route path="/terminal" element={
                        <ProtectedRoute user={user}>
                            <PermissionRoute user={user} permission="canScanProduction">
                                <TerminalView user={user} users={allUsers} onLogout={handleLogout} />
                            </PermissionRoute>
                        </ProtectedRoute>
                    } />

                    <Route path="/" element={ <ProtectedRoute user={user}> <MainLayout user={user} onLogout={handleLogout} onOpenGeneralAIChat={() => setIsGeneralChatModalOpen(true)} /> </ProtectedRoute> }>
                        <Route index element={<Navigate to="/dashboard" />} />
                        <Route path="dashboard" element={<PermissionRoute user={user} permission="canViewDashboard"><DashboardView db={db} user={user} onOpenGeneralAIChat={() => setIsGeneralChatModalOpen(true)} /></PermissionRoute>} />
                        
                        <Route path="jobs/new" element={
                            <PermissionRoute user={user} permission="canCreateJobs">
                                <NewJobStartView />
                            </PermissionRoute>
                        } />
                        <Route path="jobs/new/full" element={
                            <PermissionRoute user={user} permission="canCreateJobs">
                                <NewJobWrapper user={user} db={db} storage={storage} isEditMode={false} />
                            </PermissionRoute>
                        } />
                        <Route path="jobs/new/gang" element={
                            <PermissionRoute user={user} permission="canCreateJobs">
                                <NewGangRunView db={db} user={user} storage={storage} />
                            </PermissionRoute>
                        } />
                        <Route path="jobs/new-lite" element={
                            <PermissionRoute user={user} permission="canCreateJobs">
                                <NewJobLiteView db={db} user={user} storage={storage} />
                            </PermissionRoute>
                        } />
                        <Route path="jobs/edit/:jobId" element={
                            <PermissionRoute user={user} permission="canCreateJobs">
                                <NewJobWrapper user={user} db={db} storage={storage} isEditMode={true} />
                            </PermissionRoute>
                        } />

                        <Route path="jobs/:jobId" element={<PermissionRoute user={user} permission="canViewDashboard"><JobView db={db} user={user} storage={storage} onOpenGeneralAIChat={() => setIsGeneralChatModalOpen(true)} /></PermissionRoute>} />
                        {/* FAST MODE (Fase 1 §4.3): la ruta reservada en Fase 0 cumple su destino —
                            monta el MISMO multipieza con 1 línea y UI colapsada (sin borradores/
                            versiones). QuoteView legacy sigue comentado; getQuote CF sigue viva. */}
                        <Route path="quote" element={<PermissionRoute user={user} permission="canCreateQuotes"><ProductQuoteView db={db} user={user} fastMode /></PermissionRoute>} />
                        <Route path="quote-product" element={<PermissionRoute user={user} permission="canCreateQuotes"><ProductQuoteView db={db} user={user} /></PermissionRoute>} />
                        <Route path="estimates" element={<PermissionRoute user={user} permission="canCreateQuotes"><EstimatesView db={db} user={user} /></PermissionRoute>} />
                        {/* Cola de Trabajo (S3): gateada por canViewDashboard, el mismo permiso
                            que exige la regla de lectura de `work_queue` en firestore.rules. */}
                        <Route path="cola" element={<PermissionRoute user={user} permission="canViewDashboard"><WorkQueueView db={db} storage={storage} user={user} /></PermissionRoute>} />
                        <Route path="crm" element={<PermissionRoute user={user} permission="canAccessTradeshowCRM"><TradeshowCRMView db={db} user={user} allUsers={allUsers} /></PermissionRoute>} />
                        <Route path="clients" element={<PermissionRoute user={user} permission="canViewClients"><ClientsView db={db} user={user} /></PermissionRoute>} />
                        <Route path="materials" element={<PermissionRoute user={user} permission="canViewMaterials"><MaterialsView db={db} user={user} storage={storage} /></PermissionRoute>} />
                        <Route path="products" element={<PermissionRoute user={user} permission="canViewMaterials"><ProductsView db={db} user={user} storage={storage} /></PermissionRoute>} />
                        <Route path="machines" element={<PermissionRoute user={user} permission="canManageMachines"><MachinesView db={db} user={user} /></PermissionRoute>} />
                        <Route path="scan" element={<PermissionRoute user={user} permission="canScanProduction"><ScanningStationView user={user} users={allUsers} /></PermissionRoute>} />
                        <Route path="file-finder" element={<PermissionRoute user={user} permission="canScanProduction"><FileFinderView db={db} /></PermissionRoute>} />
                        <Route path="settings/kiosk" element={<PermissionRoute user={user} permission="canManageKiosk"><KioskSettingsView db={db} user={user} /></PermissionRoute>} />
                        <Route path="settings/pricing" element={<PermissionRoute user={user} permission="canManagePricing"><ConfiguradorPreciosView db={db} user={user} /></PermissionRoute>} />
                        <Route path="caja-diaria" element={<PermissionRoute user={user} permission="canManageInvoicing"><RouteErrorBoundary><Suspense fallback={<div className="p-4">Cargando…</div>}><CajaDiariaView db={db} user={user} /></Suspense></RouteErrorBoundary></PermissionRoute>} />
                        {/* Mi Perfil: accesible para cualquier usuario autenticado (ya no gateado por CRM). */}
                        <Route path="profile" element={<MyProfileView db={db} user={user} storage={storage} />} />
                        {/* S4c: Novedades y Feedback para cualquier autenticado; lazy + error boundary como Caja Diaria. */}
                        <Route path="novedades" element={<RouteErrorBoundary><Suspense fallback={<div className="p-4">Cargando…</div>}><NovedadesView /></Suspense></RouteErrorBoundary>} />
                        <Route path="feedback" element={<RouteErrorBoundary><Suspense fallback={<div className="p-4">Cargando…</div>}><FeedbackView db={db} user={user} /></Suspense></RouteErrorBoundary>} />
                        {/* Usuarios y Roles: una sola pantalla con pestañas; cada ruta abre su pestaña. */}
                        <Route path="users" element={<PermissionRoute user={user} permission="canManageUsers"><TeamAccessView db={db} user={user} initialTab="users" /></PermissionRoute>} />
                        <Route path="roles" element={<PermissionRoute user={user} permission="canManageRoles"><TeamAccessView db={db} user={user} initialTab="roles" /></PermissionRoute>} />
                        <Route path="*" element={<Navigate to="/dashboard" />} />
                    </Route>
                </Routes>
            </Router>
            <GeneralAiChatModal 
                isOpen={isGeneralChatModalOpen}
                onClose={() => setIsGeneralChatModalOpen(false)}
                user={user}
                apiKey={apiKey}
            />
        </>
    );
}

export default App;